/**
 * @fileoverview Target list query hook.
 * @description Wraps GET /sales/targets in a TanStack query. Used wherever a page needs targets
 * across multiple tenants at once (e.g. Sales Pipeline joining each row's Target Status client-side
 * by `tenantId`) rather than one tenant at a time (see useTenantTarget for that case).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getTargets } from '@/features/sales/services/targets.service'
import { TARGETS_QUERY_KEY } from '@/shared/constants/sales-query-keys.constants'

import type { TargetListFilters } from '@/features/sales/services/targets.service'

export { TARGETS_QUERY_KEY }

/**
 * @description Fetches targets matching the supplied filters.
 * @param {TargetListFilters} [filters] - The filters to apply.
 * @param {{ enabled?: boolean }} [options] - `enabled: false` skips the request.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useTargets(filters: TargetListFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...TARGETS_QUERY_KEY, filters],
    queryFn: () => getTargets(filters),
    enabled: options.enabled ?? true,
  })
}
